/**
 * /logs — xem log server truc tiep (muc 12 dac ta, Phase 6).
 *
 * ============================================================================
 *  CONG TAC MAC DINH TAT, va do la mot quyet dinh chu khong phai su than trong thua.
 *
 *  Dashboard duoc phuc vu tu CHINH server nay, ma HTTP/1.1 chi cho khoang 6 ket noi
 *  moi host. Mot ket noi SSE giu VINH VIEN mot suat trong so do. Neu trang nay tu mo
 *  stream luc tai, thi mo 6 tab dashboard la het han ngach va MOI request khac (tai
 *  bang, tai anh mau) se treo cho — mot loi trong nhu server bi hong.
 *
 *  Vi vay: nguoi dung phai bat, va ta tu ngat khi tab bi an.
 * ============================================================================
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api';

/** Giu bao nhieu dong tren man hinh. Vuot qua thi bo dong cu nhat. */
const MAX_ON_SCREEN = 1000;

/** Ten muc log theo so cua pino. */
const LEVEL_NAMES: Record<number, string> = {
  10: 'trace',
  20: 'debug',
  30: 'info',
  40: 'warn',
  50: 'error',
  60: 'fatal',
};

const LEVEL_CLASS: Record<number, string> = {
  10: 'text-slate-400',
  20: 'text-slate-500 dark:text-slate-400',
  30: 'text-sky-700 dark:text-sky-400',
  40: 'text-amber-700 dark:text-amber-400',
  50: 'text-red-700 dark:text-red-400',
  60: 'text-red-800 dark:text-red-300 font-semibold',
};

interface Entry {
  id: number;
  level: number;
  time: number;
  msg: string;
  rest: Record<string, unknown>;
}

let nextId = 1;

/** Doi mot dong JSON cua pino thanh muc hien duoc. `null` khi dong khong doc duoc. */
function parseLine(raw: string): Entry | null {
  try {
    const o = JSON.parse(raw) as Record<string, unknown>;
    const { level, time, msg, pid, hostname, ...rest } = o;
    void pid;
    void hostname;
    return {
      id: nextId++,
      level: typeof level === 'number' ? level : 30,
      time: typeof time === 'number' ? time : Date.now(),
      msg: typeof msg === 'string' ? msg : '',
      rest,
    };
  } catch {
    return null;
  }
}

function formatTime(ms: number): string {
  const d = new Date(ms);
  const p = (n: number, w = 2): string => String(n).padStart(w, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}.${p(d.getMilliseconds(), 3)}`;
}

export default function Logs(): JSX.Element {
  const [on, setOn] = useState(false);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [minLevel, setMinLevel] = useState(30);
  const [needle, setNeedle] = useState('');
  const [follow, setFollow] = useState(true);

  const stopRef = useRef<(() => void) | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const stop = useCallback(() => {
    stopRef.current?.();
    stopRef.current = null;
  }, []);

  useEffect(() => {
    if (!on) {
      stop();
      return;
    }
    setError(null);
    stopRef.current = api.streamEvents(
      (line) => {
        const e = parseLine(line);
        if (e === null) return;
        setEntries((prev) => {
          const next = prev.length >= MAX_ON_SCREEN ? prev.slice(prev.length - MAX_ON_SCREEN + 1) : prev;
          return [...next, e];
        });
      },
      (text) => setNotice(text),
      (message) => {
        setError(message);
        setOn(false);
      },
    );
    return stop;
  }, [on, stop]);

  // Tu ngat khi tab bi an: mot tab nen dang giu mot suat ket noi ma khong ai nhin.
  useEffect(() => {
    const onVis = (): void => {
      if (document.visibilityState === 'hidden') setOn(false);
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  const shown = useMemo(() => {
    const q = needle.trim().toLowerCase();
    return entries.filter((e) => {
      if (e.level < minLevel) return false;
      if (q === '') return true;
      return (e.msg + JSON.stringify(e.rest)).toLowerCase().includes(q);
    });
  }, [entries, minLevel, needle]);

  useEffect(() => {
    if (follow) bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [shown, follow]);

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold">Log trực tiếp</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Đọc thẳng từ server qua SSE. Các trường bí mật (API key, mật khẩu, chuỗi kết nối DB) đã
          được lọc ở hai lớp trước khi rời khỏi máy — nhưng đây vẫn là log vận hành, đừng chia sẻ
          ảnh chụp màn hình một cách tuỳ tiện.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800">
        <button
          type="button"
          onClick={() => setOn((v) => !v)}
          className={
            on
              ? 'rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700'
              : 'rounded-md bg-sky-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-sky-700'
          }
        >
          {on ? 'Dừng theo dõi' : 'Theo dõi trực tiếp'}
        </button>

        <label className="flex items-center gap-2 text-sm">
          <span className="text-slate-600 dark:text-slate-400">Mức</span>
          <select
            value={minLevel}
            onChange={(e) => setMinLevel(Number(e.target.value))}
            className="cf-input py-1"
          >
            {[10, 20, 30, 40, 50].map((lv) => (
              <option key={lv} value={lv}>
                {LEVEL_NAMES[lv]} trở lên
              </option>
            ))}
          </select>
        </label>

        <input
          value={needle}
          onChange={(e) => setNeedle(e.target.value)}
          placeholder="Lọc theo chữ…"
          className="cf-input min-w-[12rem] flex-1 py-1 text-sm"
        />

        <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
          <input type="checkbox" checked={follow} onChange={(e) => setFollow(e.target.checked)} />
          Bám đáy
        </label>

        <button
          type="button"
          onClick={() => {
            setEntries([]);
            setNotice(null);
          }}
          className="rounded-md border border-slate-300 px-3 py-1.5 text-sm dark:border-slate-600"
        >
          Xoá màn hình
        </button>

        <span className="ml-auto text-xs text-slate-500 dark:text-slate-400">
          {shown.length}/{entries.length} dòng
        </span>
      </div>

      {error !== null && (
        <p className="rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950 dark:text-red-300">
          {error}
        </p>
      )}
      {notice !== null && (
        <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950 dark:text-amber-300">
          {notice} — màn hình này đang KHÔNG đầy đủ.
        </p>
      )}

      {!on && entries.length === 0 && (
        <p className="rounded-md border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500 dark:border-slate-600 dark:text-slate-400">
          Công tắc đang tắt. Bật lên để xem log chạy trực tiếp — kèm 200 dòng gần nhất để có ngữ
          cảnh. Mặc định tắt vì mỗi kết nối giữ vĩnh viễn một suất trong số 6 kết nối mà trình
          duyệt cho phép mỗi tên miền.
        </p>
      )}

      {(on || entries.length > 0) && (
        <div className="max-h-[60vh] overflow-auto rounded-lg border border-slate-200 bg-slate-950 p-3 font-mono text-xs leading-5 dark:border-slate-700">
          {shown.map((e) => (
            <div key={e.id} className="whitespace-pre-wrap break-all">
              <span className="text-slate-500">{formatTime(e.time)}</span>{' '}
              <span className={LEVEL_CLASS[e.level] ?? 'text-slate-300'}>
                {(LEVEL_NAMES[e.level] ?? String(e.level)).padEnd(5)}
              </span>{' '}
              <span className="text-slate-100">{e.msg}</span>
              {Object.keys(e.rest).length > 0 && (
                <span className="text-slate-400"> {JSON.stringify(e.rest)}</span>
              )}
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
      )}
    </div>
  );
}
